/**
 * /campaigns — Campaigns list (Story 7.5).
 *
 * Surface:
 *   - Paginated list (default pageSize=50) with an `active` filter toggle.
 *   - "Nova campanha" button → modal form (create).
 *   - Row actions: open detail (name link), deactivate (soft delete).
 *   - Kill-switch summary column renders icons for each switch currently ON.
 *
 * The list is deliberately lightweight (no react-table) — MVP volume is
 * low. Server-side cross-FK validation surfaces as a toast with the `field`
 * pointer inlined into the message so operators know where to fix.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Power, PhoneCall, Headphones, Percent, MessageSquareReply } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, apiClient } from '@/lib/api';
import {
  CampaignForm,
  DEFAULT_CAMPAIGN_FORM,
  type CampaignFormValue,
} from '@/components/campaigns/CampaignForm';

interface CampaignDto {
  id: string;
  name: string;
  description: string | null;
  basicProductId: string | null;
  premiumProductId: string | null;
  triggerDelayHours: number;
  eligibilityWindowDays: number;
  activeHours: CampaignFormValue['activeHours'];
  templateId: string | null;
  promptId: string | null;
  kbText: string | null;
  active: boolean;
  discountEnabled: boolean;
  audioEnabled: boolean;
  followupEnabled: boolean;
  callbackEnabled: boolean;
  openaiModel: string | null;
  openaiTemperature: number | null;
  openaiMaxTokens: number | null;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  data: CampaignDto[];
  page: number;
  pageSize: number;
  total: number;
}

interface ProductOption {
  id: string;
  name: string;
  tier: 'basic' | 'premium';
}
interface TemplateOption {
  id: string;
  metaTemplateName: string;
}
interface PromptOption {
  id: string;
  name: string;
}

type ActiveFilter = '' | 'true' | 'false';

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      error?: { code?: string; message?: string; field?: string; count?: number };
    };
    const code = body?.error?.code;
    if (code === 'VALIDATION_FAILED') {
      const field = body.error?.field ? ` (campo: ${body.error.field})` : '';
      return `${body.error?.message ?? 'Validação falhou.'}${field}`;
    }
    if (code === 'CAMPAIGN_HAS_CONVERSATIONS') {
      const count = body.error?.count ?? 0;
      return `Campanha tem ${count} conversa(s) — remova-as antes de apagar.`;
    }
    if (code === 'CAMPAIGN_NOT_FOUND') {
      return 'Campanha não encontrada.';
    }
    if (body?.error?.message) {
      return body.error.message;
    }
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Build the wire-level create/update body from the form state. */
function toApiBody(v: CampaignFormValue): Record<string, unknown> {
  return {
    name: v.name,
    description: v.description,
    // Empty form value means "unset" — send explicit null so the API treats
    // it as a trigger-only/recovery campaign.
    basicProductId: v.basicProductId === '' ? null : v.basicProductId,
    premiumProductId: v.premiumProductId === '' ? null : v.premiumProductId,
    triggerDelayHours: v.triggerDelayHours,
    eligibilityWindowDays: v.eligibilityWindowDays,
    activeHours: v.activeHours,
    templateId: v.templateId,
    promptId: v.promptId,
    kbText: v.kbText,
    active: v.active,
    ...v.killSwitches,
    openaiModel: v.openaiModel,
    openaiTemperature: v.openaiTemperature,
    openaiMaxTokens: v.openaiMaxTokens,
  };
}

function KillSwitchBadges({ c }: { c: CampaignDto }) {
  const on = 'text-emerald-500';
  const off = 'text-muted-foreground/40';
  return (
    <div className="flex gap-1" aria-label="Kill switches">
      <Percent
        className={c.discountEnabled ? on : off}
        size={14}
        aria-label={`Desconto ${c.discountEnabled ? 'ON' : 'OFF'}`}
      />
      <Headphones
        className={c.audioEnabled ? on : off}
        size={14}
        aria-label={`Áudio ${c.audioEnabled ? 'ON' : 'OFF'}`}
      />
      <MessageSquareReply
        className={c.followupEnabled ? on : off}
        size={14}
        aria-label={`Follow-up ${c.followupEnabled ? 'ON' : 'OFF'}`}
      />
      <PhoneCall
        className={c.callbackEnabled ? on : off}
        size={14}
        aria-label={`Callback ${c.callbackEnabled ? 'ON' : 'OFF'}`}
      />
    </div>
  );
}

export function CampaignsPage() {
  const queryClient = useQueryClient();
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('');
  const [createOpen, setCreateOpen] = useState(false);

  const listQuery = useQuery({
    queryKey: ['admin', 'campaigns', { active: activeFilter }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('pageSize', '50');
      if (activeFilter) params.set('active', activeFilter);
      return apiClient.get<ListResponse>(`/admin/campaigns?${params.toString()}`);
    },
  });

  // Denormalization helpers: look up related entities by id so we can render
  // readable names instead of uuids.
  const allProductsQuery = useQuery({
    queryKey: ['admin', 'products', 'all-for-campaign-list'],
    queryFn: () => apiClient.get<{ data: ProductOption[] }>('/admin/products?pageSize=100'),
  });
  const templatesQuery = useQuery({
    queryKey: ['admin', 'templates', 'for-campaign-list'],
    queryFn: () => apiClient.get<{ data: TemplateOption[] }>('/admin/templates?pageSize=100'),
  });
  const promptsQuery = useQuery({
    queryKey: ['admin', 'prompts', 'for-campaign-list'],
    queryFn: () => apiClient.get<{ data: PromptOption[] }>('/admin/prompts'),
  });

  const productsById: Record<string, string> = Object.fromEntries(
    (allProductsQuery.data?.data ?? []).map((p) => [p.id, p.name] as const),
  );
  const templatesById = Object.fromEntries(
    (templatesQuery.data?.data ?? []).map((t) => [t.id, t.metaTemplateName] as const),
  );
  const promptsById = Object.fromEntries(
    (promptsQuery.data?.data ?? []).map((p) => [p.id, p.name] as const),
  );

  const createMutation = useMutation({
    mutationFn: (v: CampaignFormValue) =>
      apiClient.post<CampaignDto>('/admin/campaigns', toApiBody(v)),
    onSuccess: () => {
      toast.success('Campanha criada.');
      setCreateOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'campaigns'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao criar campanha.'));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete<CampaignDto>(`/admin/campaigns/${id}`),
    onSuccess: () => {
      toast.success('Campanha desativada.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'campaigns'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao desativar campanha.'));
    },
  });

  // Ativar campanha inline — atalho operacional (Story 7.5 AC4: partial PUT
  // com `{ active: true }`; kill switches preservam o valor atual no DB).
  const activateMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.put<CampaignDto>(`/admin/campaigns/${id}`, { active: true }),
    onSuccess: () => {
      toast.success('Campanha ativada.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'campaigns'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao ativar campanha.'));
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Campanhas</h1>
          <p className="text-sm text-muted-foreground">
            Configure o upsell: produtos, template, prompt, timing e kill switches.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1" aria-hidden />
          Nova campanha
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <select
              aria-label="Filtro active"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
            >
              <option value="">Ativas + inativas</option>
              <option value="true">Apenas ativas</option>
              <option value="false">Apenas inativas</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : listQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar campanhas.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Nome</th>
                    <th className="px-4 py-2">Básico</th>
                    <th className="px-4 py-2">Premium</th>
                    <th className="px-4 py-2">Template</th>
                    <th className="px-4 py-2">Prompt</th>
                    <th className="px-4 py-2">Ativo</th>
                    <th className="px-4 py-2">Kill switches</th>
                    <th className="px-4 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data && listQuery.data.data.length > 0 ? (
                    listQuery.data.data.map((c) => (
                      <tr key={c.id} className="border-b last:border-b-0">
                        <td className="max-w-[10rem] truncate px-2 py-2 font-medium sm:px-4">
                          <Link to={`/campaigns/${c.id}`} className="text-primary hover:underline">
                            {c.name}
                          </Link>
                        </td>
                        <td className="max-w-[10rem] truncate px-2 py-2 sm:px-4">
                          {c.basicProductId ? (productsById[c.basicProductId] ?? '—') : '—'}
                        </td>
                        <td className="max-w-[10rem] truncate px-2 py-2 sm:px-4">
                          {c.premiumProductId ? (productsById[c.premiumProductId] ?? '—') : '—'}
                        </td>
                        <td className="max-w-[10rem] truncate px-2 py-2 font-mono text-xs sm:px-4">
                          {c.templateId ? (templatesById[c.templateId] ?? '—') : '—'}
                        </td>
                        <td className="max-w-[10rem] truncate px-2 py-2 font-mono text-xs sm:px-4">
                          {c.promptId ? (promptsById[c.promptId] ?? '—') : '—'}
                        </td>
                        <td className="px-2 py-2 sm:px-4">{c.active ? 'Sim' : 'Não'}</td>
                        <td className="px-2 py-2 sm:px-4">
                          <KillSwitchBadges c={c} />
                        </td>
                        <td className="px-2 py-2 sm:px-4">
                          <div className="flex justify-end gap-1">
                            {c.active ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={deactivateMutation.isPending}
                                onClick={() => deactivateMutation.mutate(c.id)}
                                aria-label={`Desativar ${c.name}`}
                                title="Desativar"
                              >
                                <Power aria-hidden />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={activateMutation.isPending}
                                onClick={() => activateMutation.mutate(c.id)}
                                aria-label={`Ativar ${c.name}`}
                                title="Ativar"
                                className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                              >
                                <Power aria-hidden />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhuma campanha encontrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-4 overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Nova campanha</DialogTitle>
            <DialogDescription>
              Configure o upsell. Você pode ativá-la depois na lista.
            </DialogDescription>
          </DialogHeader>
          <CampaignForm
            mode="create"
            initial={DEFAULT_CAMPAIGN_FORM}
            submitting={createMutation.isPending}
            onCancel={() => setCreateOpen(false)}
            onSubmit={(next) => createMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CampaignsPage;
